/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

// Mirrors packages/backend/src/lib/authorizationPolicyBoard.ts (the API shapes).

export type Phase = 'off' | 'soak' | 'enforcing' | 'deny' | 'undetermined';

// `notRolledOut` and `blocked` are both phase `off`, split by whether the
// chart even carries the template yet (PLAT-1261 follow-up). See columnFor
// below.
export type BoardColumn =
  | 'undetermined'
  | 'notRolledOut'
  | 'blocked'
  | 'ready'
  | 'soak'
  | 'enforcing'
  | 'deny';

// The envs the promotion pipeline actually runs, in sweep order. The board's
// env selector adds 'all' on top for the combined/worst-env view.
export const ENVS = [
  'platform-ci',
  'platform-gamedev',
  'platform-staging',
  'platform-prod',
] as const;
export type EnvKey = (typeof ENVS)[number];
export type EnvFilter = EnvKey | 'all';

export const ENV_FILTER_ORDER: EnvFilter[] = [...ENVS, 'all'];

export const ENV_FILTER_LABEL: Record<EnvFilter, string> = {
  'platform-ci': 'CI',
  'platform-gamedev': 'GameDev',
  'platform-staging': 'Staging',
  'platform-prod': 'Prod',
  all: 'All environments',
};

export type RouteTier = 'management' | 'default';

export type RouteSource =
  | { kind: 'apiExposed'; exposureType: 'external' | 'internal'; useCase: string }
  | { kind: 'managementPaths' }
  | { kind: 'additionalPaths' };

export type RoutePath = { path: string; tier: RouteTier; source: RouteSource };

export type BlockerCode =
  | 'chartTooOld'
  | 'chartFamilyUnsupported'
  | 'chartRevUnknown'
  | 'notMeshed'
  | 'portUnresolved'
  | 'noGrpcPaths'
  | 'renderError';

export type Blocker = { code: BlockerCode; detail: string };

export type CallerRef = { name: string; namespace: string };

export type DefaultMode = 'authenticated' | 'unauthenticated' | 'callers';

// What the default tier admits, spelled out. `authenticated` is the chart
// default and does NOT admit unmeshed callers; `unauthenticated` does.
export const DEFAULT_MODE_LABEL: Record<DefaultMode, string> = {
  authenticated: 'any meshed caller',
  unauthenticated: 'any caller',
  callers: 'named callers',
};

export type AuthzEnvState = {
  env: string;
  path: string;
  phase: Phase;
  hasBlock: boolean;
  chartRev: string | null;
  port: string;
  managementCallers: CallerRef[];
  defaultMode: DefaultMode;
  defaultCallers: CallerRef[];
  management: RoutePath[];
  default: RoutePath[];
  renderErrors: { code: string; message: string }[];
  blockers: Blocker[];
  phase2Blockers: Blocker[];
  parseError?: string;
};

export type AuthzServiceNode = {
  name: string;
  entityRef: string;
  team: string | null;
  repo: { owner: string; name: string } | null;
  envs: AuthzEnvState[];
};

export type AuthzBoard = {
  services: AuthzServiceNode[];
  generatedAt: string;
};

// Board columns in rollout order. `notRolledOut`, `blocked`, and `ready` are
// all phase `off`; splitting them is the whole point of the board while most
// of the fleet is pre-chart. `notRolledOut` sits before `blocked`: an env
// without the chart yet hasn't reached the point where its config CAN be
// broken, so it is earlier in the timeline, not just quieter. `undetermined`
// leads for visibility only, NOT as a severity: it sits outside the rollout
// order, which is why serviceColumn excludes it from the laggard ranking
// rather than letting position imply "worst".
export const COLUMNS: BoardColumn[] = [
  'undetermined',
  'notRolledOut',
  'blocked',
  'ready',
  'soak',
  'enforcing',
  'deny',
];

export const COLUMN_LABEL: Record<BoardColumn, string> = {
  undetermined: 'Undetermined',
  notRolledOut: 'Not rolled out',
  blocked: 'Blocked',
  ready: 'Ready',
  soak: 'Phase 1 - Soak',
  enforcing: 'Phase 2 - Enforcing',
  deny: 'Phase 3 - Deny',
};

export const COLUMN_HELP: Record<BoardColumn, string> = {
  undetermined:
    'accessPolicy is a legal Linkerd value the rollout model does not cover, so no phase is claimed and no advance is offered. Not a severity: all-unauthenticated is more permissive than a soak, cluster-authenticated is stricter. Read the value before acting; set audit or deny to rejoin the model.',
  notRolledOut:
    'The chart pin predates spring-microservice-0.19.0, or there is no linkerd.authorizationPolicy block at all. Expected during the env-major sweep; not actionable here.',
  blocked:
    'The chart supports the policy, but the config is half-staged or would trip a guard on render. Actionable, see the blockers on the card.',
  ready: 'Eligible to start the soak; the values block is not enabled.',
  soak: 'Server only, accessPolicy audit. Nothing is denied.',
  enforcing:
    'Routes are live and enforced verbatim, even under audit. This is the real cutover.',
  deny: 'Server-level allow-all removed. Closes the connection-level gap.',
};

// Blocker codes that mean the chart itself isn't there yet, not that the
// config is broken. Mirrors CHART_GATE_CODES in the backend's
// authorizationPolicyBoard.ts.
const CHART_GATE_CODES: ReadonlySet<BlockerCode> = new Set([
  'chartTooOld',
  'chartFamilyUnsupported',
  'chartRevUnknown',
]);

export const columnFor = (env: AuthzEnvState): BoardColumn => {
  if (env.phase !== 'off') return env.phase;
  // No block at all is the same "hasn't gotten here yet" state as a too-old
  // chart, even if the chart itself would already support one.
  if (!env.hasBlock || env.blockers.some(b => CHART_GATE_CODES.has(b.code))) {
    return 'notRolledOut';
  }
  return env.blockers.length > 0 ? 'blocked' : 'ready';
};

// Worst column across a service's envs, for the card's headline position.
// Ordered by rollout progress, so a service at Phase 3 in ci and blocked in
// prod sorts as blocked: the laggard is the actionable end.
//
// `undetermined` envs are EXCLUDED from the ranking rather than ranked worst.
// The four accessPolicy values behind it do not share a risk level:
// `all-unauthenticated` is Linkerd's own default inbound policy and so is more
// permissive than a Phase 1 soak, while `cluster-authenticated` requires mTLS
// and is stricter than the `audit` Phase 1 runs on. Ranking them together as
// the fleet laggard would assert an ordering the model explicitly withholds.
export const serviceColumn = (node: AuthzServiceNode): BoardColumn => {
  if (node.envs.length === 0) return 'blocked';
  const ranks = node.envs
    .filter(e => columnFor(e) !== 'undetermined')
    .map(e => COLUMNS.indexOf(columnFor(e)));
  // Every env undetermined: nothing rankable is left, so report it rather than
  // fall through to an out-of-range index.
  if (ranks.length === 0) return 'undetermined';
  return COLUMNS[Math.min(...ranks)];
};

/**
 * Column for one service under an env-selector value (PLAT-1261 follow-up).
 * `all` keeps the existing worst-env aggregate above; a specific env
 * classifies and ranks the service by that env's row ALONE, which is the
 * whole point of the selector: an env-major sweep means gamedev/prod sit at
 * `notRolledOut` for a long time regardless of how far ci has gotten.
 *
 * Returns null when the service has no row for that env at all, so callers
 * can drop it from that env's board rather than guess a column for data that
 * was never read.
 */
export const columnForFilter = (
  node: AuthzServiceNode,
  filter: EnvFilter,
): BoardColumn | null => {
  if (filter === 'all') return serviceColumn(node);
  const env = node.envs.find(e => e.env === filter);
  return env ? columnFor(env) : null;
};

export const BLOCKER_LABEL: Record<BlockerCode, string> = {
  chartTooOld: 'chart too old',
  chartFamilyUnsupported: 'chart family',
  chartRevUnknown: 'no chart pin',
  notMeshed: 'not meshed',
  portUnresolved: 'port unresolved',
  noGrpcPaths: 'no gRPC paths',
  renderError: 'render error',
};
